import {
  Container,
  Box,
  Divider,
  Stack,
  Button,
  IconButton,
  Skeleton,
  CircularProgress,
} from '@mui/material';

import Typography from '@mui/material/Typography';
import { Remove, Add, Delete } from '@mui/icons-material';

import DeleteIcon from '@mui/icons-material/Delete';
import { environment } from 'apps/table-tuck-web/src/environments/environment';
import React, { useEffect, useState, memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckItem } from '@tabletuck/redux-slices';
import { useTranslation } from 'react-i18next';
import logo from 'apps/table-tuck-web/src/assets/fattyBattyLogo.png';
import { useCartItemActions } from '../../hooks/useCartItemActions';
const CartDrawerCard = ({
  item,
  typeState,
  itemErr,
  errDelivery,
  setErrDelivery,
  lineItemData,
}) => {
  const items = useSelector((state) => state.shopReducer.currentItem);
  const [t, i18n] = useTranslation();

  // Resolve image: prefer API lineItem image, otherwise find matching menu item by variation/item id
  const resolvedImage = useMemo(() => {
    // API might use different fields for image
    const apiImg =
      item?.item_image_url ||
      item?.image ||
      item?.image_url ||
      item?.itemImageUrl;
    if (apiImg) return apiImg;

    if (!Array.isArray(items) || items.length === 0) return '';

    // Try to find the menu item matching this lineItem
    const found = items.find((mi) => {
      if (!mi) return false;
      // direct item id match
      if (mi.id && (mi.id === item?.variationId || mi.id === item?.itemId))
        return true;
      // some datasets keep variation ids nested on the item
      if (Array.isArray(mi.variations)) {
        if (
          mi.variations.some(
            (v) => v?.id === item?.variationId || v?.id === item?.itemId
          )
        )
          return true;
      }
      // fallback name match
      if (
        mi.name &&
        item?.name &&
        mi.name.toLowerCase() === item.name.toLowerCase()
      )
        return true;
      return false;
    });

    if (!found) return '';

    // prefer explicit item_image_url, otherwise first entry from listOfImages
    return (
      found.item_image_url ||
      (Array.isArray(found.listOfImages) && found.listOfImages.length > 0
        ? found.listOfImages[0]
        : '')
    );
  }, [item, items]);

  const dispatch = useDispatch();

  // Use the shared cart item actions hook
  const {
    quantity: value,
    loading,
    isUpdatingPrice,
    handleQuantityChange,
    handleItemRemove,
  } = useCartItemActions(item, {
    maxQuantity: 10,
  });

  const checkItem = () => {
    setErrDelivery(''); // Clear any existing error message
    dispatch(CheckItem('')); // Clear the error using the Redux action
    if (item) {
      // Try multiple matching strategies to find the menu item
      const foundItem = items?.find(
        (i) =>
          i?.id === item.variationId ||
          i?.variationId === item.variationId ||
          i?.variations?.some((v) => v.id === item.variationId) ||
          // Fallback: try name matching if IDs don't work
          i?.name?.toLowerCase() === item.name?.toLowerCase()
      );

      if (!foundItem) {
        setErrDelivery(t('basket.itemNotAvailable'));
        dispatch(CheckItem('This item is not available'));
      } else {
        if (!foundItem?.delivery_enabled && typeState === 'delivery') {
          setErrDelivery(t('basket.itemNotAvailableForDelivery'));
          dispatch(CheckItem('This item is not available for delivery'));
        } else if (!foundItem?.pickup_enabled && typeState === 'pickup') {
          setErrDelivery(t('basket.itemNotAvailableToPickup'));
          dispatch(CheckItem('This item is not available for pickup'));
        } else if (!foundItem?.dinein_enabled && typeState === 'dinein') {
          setErrDelivery(t('basket.itemNotAvailableDineIn'));
          dispatch(CheckItem('This item is not available for dine-in'));
        } else {
          dispatch(CheckItem(''));
          setErrDelivery('');
        }
      }
    }
  };

  useEffect(() => {
    checkItem();
  }, [typeState]);

  return (
    item && (
      <Container 
        maxWidth="lg"
        sx={{ cursor: 'default' }}
      >
        <Box
          sx={{
            marginBottom: '15px',
            marginTop: '20px',
            position: 'relative',
            cursor: 'default',
          }}
        >
          <IconButton
            onClick={() => handleItemRemove(item)}
            disabled={loading || isUpdatingPrice}
              sx={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                width: '32px',
                height: '32px',
                backgroundColor: loading ? '#f5f5f5' : '#ffebee',
                color: loading ? '#999' : '#d32f2f',
                border: '1px solid',
                borderColor: loading ? '#e0e0e0' : '#ffcdd2',
                cursor: loading ? 'default' : 'pointer',
                '&:hover': {
                  backgroundColor: loading ? '#f5f5f5' : '#ffcdd2',
                  borderColor: loading ? '#e0e0e0' : '#f44336',
                  transform: loading ? 'none' : 'scale(1.05)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            aria-label="delete item"
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: environment.mainColor }} />
            ) : (
              <DeleteIcon fontSize="small" />
            )}
          </IconButton>
          <p style={{ color: 'red' }}>{errDelivery}</p>
          <Box
            sx={{
              backgroundColor: '#F3F6F9',
              p: 2,
              borderRadius: '20px',
              maxWidth: { xs: '100%', lg: '400px' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: 2,
              cursor: 'default',
            }}
          >
            <Stack direction="row" gap={2} sx={{ cursor: 'default' }}>
              <img
                src={resolvedImage || item?.image || logo}
                alt={item?.title || item?.name}
                style={{
                  width: '58px',
                  height: '58px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  cursor: 'default',
                }}
              />
              <Stack direction="column" alignItems="flex-start" gap={0.5} sx={{ cursor: 'default' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    color: '#172B4D',
                    fontSize: '16px',
                    textTransform: 'capitalize',
                    fontFamily: 'Jost, sans-serif',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    gap: 0.5,
                  }}
                >
                  {item?.name || item?.title}
                  {(lineItemData?.variationName || item?.variationName) && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '13px',
                        color: '#5C6B7A',
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 500,
                        fontStyle: 'italic',
                      }}
                    >
                      {` ( ${lineItemData?.variationName || item?.variationName} )`}
                    </Box>
                  )}
                </Typography>

                {/* Display modifiers from API lineItem structure */}
                {item?.modifiers && item.modifiers.length > 0 && (
                  <Box sx={{ marginTop: 0.5 }}>
                    {item.modifiers.map((modifier, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          color: '#666',
                          fontFamily: 'Jost, sans-serif',
                          textTransform: 'capitalize',
                          lineHeight: 1.2,
                        }}
                      >
                        {/* Show modifier name with quantity in parentheses when available */}
                        • {modifier.name}
                        {modifier.quantity ? ` (x${modifier.quantity})` : ''}
                        {modifier.totalPriceMoney?.amount &&
                          parseFloat(modifier.totalPriceMoney.amount) > 0 && (
                            <span
                              style={{ color: '#2C742F', fontWeight: '600' }}
                            >
                              {' '}
                              (+$
                              {(
                                parseFloat(modifier.totalPriceMoney.amount) /
                                100
                              ).toFixed(2)}
                              )
                            </span>
                          )}
                      </Typography>
                    ))}
                  </Box>
                )}

                {isUpdatingPrice ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    height={24}
                    sx={{
                      marginTop: item?.modifiers?.length > 0 ? '8px' : '0',
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{
                      fontSize: '18px',
                      fontWeight: '800',
                      fontFamily: 'Jost, sans-serif',
                      color: '#172B4D',
                      marginTop: item?.modifiers?.length > 0 ? '8px' : '0',
                    }}
                  >
                    $
                    {item?.grossSalesMoney
                      ? (parseFloat(item.grossSalesMoney) / 100).toFixed(2)
                      : (item?.calculatedPrice || 0).toFixed(2)}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Stack
              direction="column"
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {/* Delete Button */}

              {/* Quantity Controls */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: '8px',
                  width: { xs: '100%', sm: '112px' },
                  backgroundColor: '#FFFFFF',
                  borderRadius: '170px',
                  cursor: 'default',
                }}
              >
                <IconButton
                  onClick={() => {
                    if (value > 1 && !isUpdatingPrice) {
                      handleQuantityChange('dec', item);
                    }
                  }}
                  disabled={value <= 1 || isUpdatingPrice}
                  sx={{
                    width: '28px',
                    height: '28px',
                    backgroundColor:
                      value <= 1 || isUpdatingPrice ? '#f5f5f5' : '#E4E3E3',
                    color: value <= 1 || isUpdatingPrice ? '#999' : '#172B4D',
                    cursor: value <= 1 || isUpdatingPrice ? 'default' : 'pointer',
                    '&:hover': {
                      backgroundColor:
                        value <= 1 || isUpdatingPrice ? '#f5f5f5' : '#d5d5d5',
                    },
                  }}
                  aria-label="decrease count"
                >
                  {isUpdatingPrice ? (
                    <CircularProgress size={14} sx={{ color: '#999' }} />
                  ) : (
                    <Remove fontSize="small" />
                  )}
                </IconButton>

                <Typography
                  variant="h4"
                  component="span"
                  sx={{
                    color: '#172B4D',
                    textAlign: 'center',
                    fontSize: '16px',
                    minWidth: '24px',
                  }}
                >
                  {value}
                </Typography>

                <IconButton
                  onClick={() => {
                    if (value < 10 && !isUpdatingPrice) {
                      handleQuantityChange('inc', item);
                    }
                  }}
                  disabled={value >= 10 || isUpdatingPrice}
                  sx={{
                    width: '28px',
                    height: '28px',
                    backgroundColor:
                      value >= 10 || isUpdatingPrice
                        ? '#f5f5f5'
                        : environment.mainColor,
                    color: value >= 10 || isUpdatingPrice ? '#999' : 'white',
                    cursor: value >= 10 || isUpdatingPrice ? 'default' : 'pointer',
                    '&:hover': {
                      backgroundColor:
                        value >= 10 || isUpdatingPrice
                          ? '#f5f5f5'
                          : environment.mainColor,
                      opacity: value >= 10 || isUpdatingPrice ? 1 : 0.9,
                    },
                  }}
                  aria-label="increase count"
                >
                  {isUpdatingPrice ? (
                    <CircularProgress size={14} sx={{ color: '#999' }} />
                  ) : (
                    <Add fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Stack>
          </Box>
        </Box>

        {environment.restaurantId !== 'fattyPattyDev' &&
          environment.restaurantId !== 'fattyPatty' && (
            <Divider style={{ marginTop: '5px' }} />
          )}
      </Container>
    )
  );
};
export default memo(CartDrawerCard);
